// src/context/AccountContext.tsx
import React, { createContext, useState, useContext, ReactNode } from 'react';
import { Account } from '../state/types';

interface AccountContextType {
  showSignUp: boolean;
  setShowSignUp: (show: boolean) => void;
  selectedAccount: Account | null;
  setSelectedAccount: (account: Account | null) => void;
  isChangePasswordOpen: boolean;
  setIsChangePasswordOpen: (isOpen: boolean) => void;
  openAddAccountModal: () => void;
  openEditAccountModal: (account: Account) => void;
  closeModal: () => void;
}

const AccountContext = createContext<AccountContextType | undefined>(undefined);

export const AccountProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [showSignUp, setShowSignUp] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);

  const openAddAccountModal = () => {
    setSelectedAccount(null);
    setShowSignUp(true);
  };

  const openEditAccountModal = (account: Account) => {
    setSelectedAccount(account);
    setShowSignUp(true);
  };

  const closeModal = () => {
    setSelectedAccount(null);
    setShowSignUp(false);
  };

  const value = {
    showSignUp,
    setShowSignUp,
    selectedAccount,
    setSelectedAccount,
    isChangePasswordOpen,
    setIsChangePasswordOpen,
    openAddAccountModal,
    openEditAccountModal,
    closeModal,
  };

  return (
    <AccountContext.Provider value={value}>
      {children}
    </AccountContext.Provider>
  );
};

export const useAccountContext = () => {
  const context = useContext(AccountContext);
  if (context === undefined) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
};